"use client";

import { motion } from "framer-motion";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

// Reusing your BulletPoint component
interface BulletPointProps {
  children: React.ReactNode;
  number?: number;
}

const BulletPoint = ({ children, number }: BulletPointProps) => (
  <li className="relative pl-6 mb-2">
    {number ? (
      <span className="absolute left-0 top-0 flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: colors.primaryLight }}>
        {number}
      </span>
    ) : (
      <span className="absolute left-0 top-2 h-2 w-2 rounded-full" style={{ backgroundColor: colors.primaryLight }}></span>
    )}
    {children}
  </li>
);

export default function TermsOfService() {
  return (
    <section className="py-20" style={{ backgroundColor: colors.white }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-16 text-center"
        >
          <span className="mb-4 inline-block rounded-full px-6 py-2 text-sm font-semibold uppercase tracking-wider" style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}>
            Effective Date: [Date]
          </span>
          <h2 className="text-4xl font-bold sm:text-5xl" style={{ color: colors.textDark }}>
            Terms of
            <span className="bg-gradient-to-r bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}>
              {" "}Service
            </span>
          </h2>
          <div className="mx-auto mt-6 h-1.5 w-24 rounded-full" style={{ background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryLight})` }}></div>
          <p className="mx-auto mt-6 max-w-2xl text-lg" style={{ color: colors.textDark }}>
            Please read these terms carefully before using our services
          </p>
        </motion.div>

        {/* Terms Content */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="prose-lg mx-auto max-w-3xl"
          style={{ color: colors.textDark }}
        >
          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-4" style={{ color: colors.primary }}>1. Acceptance of Terms</h3>
            <p>
              By accessing or using Yegna Microfinance's services, you agree to be bound by these Terms 
              of Service. If you disagree with any part, you may not access our services.
            </p>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-4" style={{ color: colors.primary }}>2. Service Eligibility</h3>
            <ul className="space-y-2">
              <BulletPoint>Must be at least 18 years old</BulletPoint>
              <BulletPoint>Valid government-issued ID required</BulletPoint>
              <BulletPoint>Resident of service areas we operate in</BulletPoint>
            </ul>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-4" style={{ color: colors.primary }}>3. User Responsibilities</h3>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <h4 className="mb-3 font-semibold" style={{ color: colors.primary }}>Prohibited Actions</h4>
                <ul className="space-y-2">
                  <BulletPoint>Fraudulent activities</BulletPoint>
                  <BulletPoint>Money laundering</BulletPoint>
                  <BulletPoint>Unauthorized access</BulletPoint>
                </ul>
              </div>
              <div>
                <h4 className="mb-3 font-semibold" style={{ color: colors.primary }}>Required Actions</h4>
                <ul className="space-y-2">
                  <BulletPoint>Accurate information</BulletPoint>
                  <BulletPoint>Timely payments</BulletPoint>
                  <BulletPoint>Security maintenance</BulletPoint>
                </ul>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-4" style={{ color: colors.primary }}>4. Intellectual Property</h3>
            <ol className="space-y-3">
              <BulletPoint number={1}>
                All content © Yegna Microfinance unless otherwise stated
              </BulletPoint>
              <BulletPoint number={2}>
                Trademarks protected under Ethiopian law
              </BulletPoint>
              <BulletPoint number={3}>
                Limited license for personal, non-commercial use
              </BulletPoint>
            </ol>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-4" style={{ color: colors.primary }}>5. Limitations of Liability</h3>
            <div className="rounded-lg p-6" style={{ backgroundColor: `${colors.primary}10`, border: `1px solid ${colors.primary}20` }}>
              <ul className="space-y-3">
                <BulletPoint>Not liable for indirect damages</BulletPoint>
                <BulletPoint>Maximum liability limited to fees paid</BulletPoint>
                <BulletPoint>Force majeure exceptions</BulletPoint>
              </ul>
            </div>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-4" style={{ color: colors.primary }}>6. Termination</h3>
            <p>
              We reserve the right to suspend or terminate access to our services:
            </p>
            <ul className="mt-4 space-y-2">
              <BulletPoint>Breach of terms</BulletPoint>
              <BulletPoint>Fraudulent activity</BulletPoint>
              <BulletPoint>Legal requirements</BulletPoint>
            </ul>
          </div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="mt-20 text-center"
          >
            <div className="inline-block rounded-2xl p-0.5 shadow-lg" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})` }}>
              <div className="rounded-2xl px-8 py-8 sm:px-12 sm:py-10" style={{ backgroundColor: colors.white }}>
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full text-3xl" style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    className="h-8 w-8"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                </div>
                <h3 className="mt-6 text-2xl font-bold" style={{ color: colors.textDark }}>
                  Terms Questions?
                </h3>
                <p className="mt-3" style={{ color: colors.textDark }}>
                  Contact our legal department
                </p>
                <div className="mt-6">
                  <a 
                    href="mailto:legal@yegnamicrofinance.com" 
                    className="font-medium transition-colors hover:opacity-80"
                    style={{ color: colors.primary }}
                  >
                    legal@yegnamicrofinance.com
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}